import { registerMockAPI } from '~/mocks/mockApis';

import { useMutation } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import type {
  CollectingRequestDetail,
  GetCollectingRequestDetailParams,
  GetCollectingRequestDetailResponse,
} from '~/types/delivery-agency.types';

const fetchCollectingRequestDetail = async (
  params: GetCollectingRequestDetailParams
): Promise<CollectingRequestDetail> => {
  const { data } = await axiosInstance.get<GetCollectingRequestDetailResponse>(
    `${API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUEST_DETAIL}/${params.collectingRequestCode}`
  );
  return data.data;
};

export const useGetCollectingRequestDetailMutation = (options?: { showGlobalDimOverlay?: boolean }) => {
  return useMutation({
    mutationFn: fetchCollectingRequestDetail,
    meta: { showGlobalDimOverlay: options?.showGlobalDimOverlay ?? true },
  });
};

registerMockAPI<GetCollectingRequestDetailResponse>({
  method: 'get',
  endpoint: `${API_ENDPOINT.DELIVERY_AGENCY.GET_COLLECTING_REQUEST_DETAIL}/:collectingRequestCode([A-Z]{0,}\\d[A-Z0-9]{0,})`,
  label: '배송 주문 반품 신청·조회/상세',
  scenarios: [
    {
      name: '회수 예정 (운송장 1건)',
      response: {
        data: {
          collectingRequestCode: 'RR20260804001',
          originalOutboundCode: 'OB20260803001',
          clientOrderCodes: ['ORD-20260803-0001'],
          collectingRequestedAt: '2026-08-04 10:12:33',
          contact: { name: '홍**', tel: '010-****-1234' },
          collectingSite: {
            primaryAddress: '서울시 강남구 테헤란로 133',
            secondaryAddress: '18층 1801호',
            deliveryLocation: { code: 'DOORSTEP', description: '문 앞' },
            deliveryLocationDetail: '',
            entranceMethod: { code: 'PUBLIC_DOOR_PWD', description: '공동현관 비밀번호' },
            entranceMethodDetail: '#1234',
          },
          productName: '유리컵 2개',
          boxCount: 1,
          collectedProductHandling: { code: 'DISPOSE', description: '배송사 폐기' },
          sendAlimTalkImmediately: true,
          collectingShippingLabels: [
            {
              shippingLabelNumber: 'CL20260804001',
              collectingShippingLabelStatus: { code: 'COLLECT_ACCEPTED', description: '회수 예정' },
              statusUpdatedAt: null,
              failureReason: null,
            },
          ],
        },
      },
    },
    {
      name: '회수 완료(부분) — 운송장 3건, 실패 사유 포함',
      response: {
        data: {
          collectingRequestCode: 'RR20260803004',
          originalOutboundCode: 'OB20260802004',
          clientOrderCodes: ['ORD-20260802-0004', 'ORD-20260802-0005', 'ORD-20260802-0006'],
          collectingRequestedAt: '2026-08-03 09:30:12',
          contact: { name: '이**', tel: '010-****-5678' },
          collectingSite: {
            primaryAddress: '경기도 성남시 분당구 판교역로 235',
            secondaryAddress: '',
            deliveryLocation: { code: 'DELIVERY_BOX', description: '택배 수령실' },
            deliveryLocationDetail: '지하 1층 무인택배함 A-12',
            entranceMethod: { code: 'ETC', description: '기타' },
            entranceMethodDetail: '경비실에 문의',
          },
          productName: '접시 세트',
          boxCount: 3,
          collectedProductHandling: { code: 'RETURN_TO_SENDER', description: '다시 돌려받기(반송)' },
          sendAlimTalkImmediately: false,
          collectingShippingLabels: [
            {
              shippingLabelNumber: 'CL20260803004',
              collectingShippingLabelStatus: { code: 'COLLECT_COMPLETED', description: '회수 완료' },
              statusUpdatedAt: '2026-08-03 18:44:02',
              failureReason: null,
            },
            {
              shippingLabelNumber: 'CL20260803005',
              collectingShippingLabelStatus: { code: 'COLLECT_FAILED', description: '회수 실패' },
              statusUpdatedAt: '2026-08-03 18:50:11',
              failureReason: { code: 'PRODUCT_NOT_FOUND', description: '회수 장소에 상품 없음' },
            },
            {
              shippingLabelNumber: 'CL20260803006',
              collectingShippingLabelStatus: { code: 'COLLECT_CANCELED', description: '회수 취소' },
              statusUpdatedAt: '2026-08-03 11:10:55',
              failureReason: null,
            },
          ],
        },
      },
    },
    {
      name: '운송장 없음 (빈 값 하이픈 확인)',
      response: {
        data: {
          collectingRequestCode: 'RR20260802007',
          originalOutboundCode: 'OB20260801007',
          clientOrderCodes: [],
          collectingRequestedAt: '2026-08-02 17:22:38',
          contact: { name: '정**', tel: '010-****-9012' },
          collectingSite: {
            primaryAddress: '부산시 해운대구 센텀중앙로 79',
            secondaryAddress: '',
            deliveryLocation: { code: 'ETC', description: '기타' },
            deliveryLocationDetail: '',
            entranceMethod: { code: 'FREE_ACCESS', description: '자유출입' },
            entranceMethodDetail: '',
          },
          productName: '샐러드 볼',
          boxCount: 1,
          collectedProductHandling: { code: 'DISPOSE', description: '배송사 폐기' },
          sendAlimTalkImmediately: false,
          collectingShippingLabels: [],
        },
      },
    },
    {
      name: '서버 에러 (500)',
      status: 500,
      response: {} as GetCollectingRequestDetailResponse,
    },
  ],
});
